import { createSelector } from "reselect";
import {
  selectCurrentCustomerSubscription,
  selectCurrentSubscriptionDetail,
  selectCurrentSubscriptionDetailLoaded,
} from "../subscriptions";
import { selectCurrentSKUDetails, selectFeatureSKUDetail } from "../skuMapping";
import { ISKUDetails } from "../../services/api/offering/types";
import { selectBrandCode } from "../metadata";
import { INextInvoice } from "../../types/common";
import { getAddonType } from "../../pages/Subscriptions/utils";
import { LicenseType } from "../../types/license";

export const selectNextInvoiceData = createSelector(
  [selectCurrentSubscriptionDetail, selectFeatureSKUDetail],
  (subscriptionDetail, featureSKU): INextInvoice | undefined => {
    const featurePrice = featureSKU?.sp?.unit_cost.find((item) => {
      return (
        item.currency === subscriptionDetail?.sp.next_invoice_amount?.currency
      );
    });
    const nextInvoicesAmount = featurePrice
      ? featurePrice
      : subscriptionDetail?.sp.next_invoice_amount;

    return {
      // Lets consumers check these figures are for the subscription being rendered.
      subscriptionId: subscriptionDetail?.subscription_id,
      nextInvoicesAmount,
      nextInvoicesAt: subscriptionDetail?.sp.next_invoices_at,
    };
  },
);

export const selectCurrentSKUAddonsByType = createSelector(
  [selectCurrentSKUDetails, selectCurrentCustomerSubscription],
  (currentSKUDetails, customerSubscription) => {
    if (!currentSKUDetails || !customerSubscription) {
      return { userLicenseAddon: undefined, serverLicenseAddon: undefined };
    }

    const currentSKU = currentSKUDetails?.items.find((item) => {
      return item.sku_id === customerSubscription.sku_id;
    });

    const SKUAddonList = currentSKUDetails?.items.filter((item) => {
      return currentSKU?.addons.includes(item.sku_id);
    });

    const userLicenseAddon = SKUAddonList?.find((addon) => {
      return getAddonType(addon.sp.sp_sku_code) === LicenseType.User;
    });

    const serverLicenseAddon = SKUAddonList?.find((addon) => {
      return getAddonType(addon.sp.sp_sku_code) === LicenseType.Server;
    });

    return { userLicenseAddon, serverLicenseAddon };
  },
);

export const selectCurrentSKUAddon = createSelector(
  [selectCurrentSKUAddonsByType],
  ({ userLicenseAddon }): ISKUDetails | undefined => {
    // Maintain backwards compatibility by returning user license addon
    return userLicenseAddon;
  },
);

export const selectCurrentUserLicenseAddon = createSelector(
  [selectCurrentSKUAddonsByType],
  ({ userLicenseAddon }): ISKUDetails | undefined => {
    return userLicenseAddon;
  },
);

export const selectCurrentServerLicenseAddon = createSelector(
  [selectCurrentSKUAddonsByType],
  ({ serverLicenseAddon }): ISKUDetails | undefined => {
    return serverLicenseAddon;
  },
);

export const selectIsCurrentSubscriptionReady = createSelector(
  [selectCurrentSubscriptionDetailLoaded, selectBrandCode],
  (isSubscriptionLoaded, brandCode): boolean => {
    return brandCode === "ADMIN" || isSubscriptionLoaded;
  },
);
